import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Handshake, PhoneCall, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const ESPERIENZE = [
  { v: "commerciale", l: "Commerciale" },
  { v: "installatore", l: "Installatore" },
  { v: "agenzia", l: "Agenzia" },
  { v: "altro", l: "Altro" },
];

const TIPOLOGIE = [
  { v: "casa", l: "Casa" },
  { v: "azienda", l: "Azienda" },
  { v: "altro", l: "Altro" },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-muted-foreground">{label}</label>
    {children}
  </div>
);

export default function LandingQuickActions() {
  const [openPartner, setOpenPartner] = useState(false);
  const [openLead, setOpenLead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [donePartner, setDonePartner] = useState(false);
  const [doneLead, setDoneLead] = useState(false);

  const [pf, setPf] = useState({ nome: "", azienda: "", provincia: "", telefono: "", email: "", esperienza: "commerciale", messaggio: "", hp: "" });
  const [lf, setLf] = useState({ nome: "", telefono: "", email: "", comune_cap: "", tipologia: "casa", messaggio: "", hp: "" });
  const setP = (k) => (e) => setPf((s) => ({ ...s, [k]: e.target.value }));
  const setL = (k) => (e) => setLf((s) => ({ ...s, [k]: e.target.value }));

  const submitPartner = async () => {
    if (pf.nome.trim().length < 3) return toast.error("Inserisci nome e cognome");
    if (!pf.provincia.trim()) return toast.error("Inserisci la provincia operativa");
    if (!pf.telefono.trim()) return toast.error("Inserisci il telefono");
    if (!pf.email.includes("@")) return toast.error("Inserisci un'email valida");
    setBusy(true);
    try {
      await api.post("/public/partner-candidate", pf);
      setDonePartner(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitLead = async () => {
    if (lf.nome.trim().length < 2) return toast.error("Inserisci il tuo nome");
    if (!lf.telefono.trim()) return toast.error("Inserisci il telefono");
    if (!lf.email.includes("@")) return toast.error("Inserisci un'email valida");
    if (!lf.comune_cap.trim()) return toast.error("Inserisci l'indirizzo");
    setBusy(true);
    try {
      await api.post("/public/quick-lead", lf);
      if (window.fbq) window.fbq("track", "Lead", { content_name: "Richiesta di ricontatto" });
      setDoneLead(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5" data-testid="landing-quick-actions">
      <div className="relative my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">oppure</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setOpenPartner(true)}
          data-testid="cta-diventa-partner"
          className="group flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-4 text-left transition-colors hover:border-primary/50 hover:bg-secondary/50"
        >
          <Handshake className="h-6 w-6 text-primary shrink-0 mt-0.5" strokeWidth={1.8} />
          <div className="leading-tight">
            <div className="text-sm font-semibold flex items-center gap-1">Diventa Partner Commerciale <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" /></div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Entra nella rete vendita TORRI.solar.</div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setOpenLead(true)}
          data-testid="cta-preferisco-ricontatto"
          className="group flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-4 text-left transition-colors hover:border-primary/50 hover:bg-secondary/50"
        >
          <PhoneCall className="h-6 w-6 text-primary shrink-0 mt-0.5" strokeWidth={1.8} />
          <div className="leading-tight">
            <div className="text-sm font-semibold flex items-center gap-1">Preferisco essere ricontattato <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" /></div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Ti richiamiamo noi, senza configurare nulla.</div>
          </div>
        </button>
      </div>

      {/* DIVENTA PARTNER */}
      <Dialog open={openPartner} onOpenChange={(v) => { setOpenPartner(v); if (!v) setDonePartner(false); }}>
        <DialogContent className="max-w-lg" data-testid="partner-candidate-dialog">
          {!donePartner ? (
            <>
              <DialogHeader>
                <DialogTitle>Diventa Partner Commerciale</DialogTitle>
                <DialogDescription>Lasciaci i tuoi dati: il Responsabile Rete Partner ti contatterà entro 24 ore.</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 mt-1">
                <div className="sm:col-span-2"><Field label="Nome e Cognome *"><Input value={pf.nome} onChange={setP("nome")} data-testid="pf-nome" /></Field></div>
                <Field label="Azienda (facoltativa)"><Input value={pf.azienda} onChange={setP("azienda")} data-testid="pf-azienda" /></Field>
                <Field label="Provincia operativa *"><Input value={pf.provincia} onChange={setP("provincia")} placeholder="Es. Brescia" data-testid="pf-provincia" /></Field>
                <Field label="Telefono *"><Input value={pf.telefono} onChange={setP("telefono")} data-testid="pf-telefono" /></Field>
                <Field label="Email *"><Input type="email" value={pf.email} onChange={setP("email")} data-testid="pf-email" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Esperienza">
                    <Select value={pf.esperienza} onValueChange={(v) => setPf((s) => ({ ...s, esperienza: v }))}>
                      <SelectTrigger data-testid="pf-esperienza"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ESPERIENZE.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`pf-esperienza-${o.v}`}>{o.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="sm:col-span-2"><Field label="Messaggio (facoltativo)"><Textarea value={pf.messaggio} onChange={setP("messaggio")} rows={3} data-testid="pf-messaggio" /></Field></div>
              </div>
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                <input tabIndex={-1} autoComplete="off" value={pf.hp} onChange={setP("hp")} data-testid="pf-hp" />
              </div>
              <DialogFooter>
                <Button onClick={submitPartner} disabled={busy} data-testid="pf-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia candidatura"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-center" data-testid="pf-success">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg font-bold">Candidatura inviata!</h3>
              <p className="text-sm text-muted-foreground mt-1">Grazie. Il Responsabile Rete Partner ti contatterà entro 24 ore.</p>
              <Button variant="outline" className="mt-4" onClick={() => setOpenPartner(false)} data-testid="pf-close">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PREFERISCO ESSERE RICONTATTATO */}
      <Dialog open={openLead} onOpenChange={(v) => { setOpenLead(v); if (!v) setDoneLead(false); }}>
        <DialogContent className="max-w-lg" data-testid="quick-lead-dialog"
          onPointerDownOutside={(e) => { if (e.detail?.originalEvent?.target?.closest?.(".pac-container")) e.preventDefault(); }}
          onInteractOutside={(e) => { if (e.detail?.originalEvent?.target?.closest?.(".pac-container")) e.preventDefault(); }}>
          {!doneLead ? (
            <>
              <DialogHeader>
                <DialogTitle>Preferisco essere ricontattato</DialogTitle>
                <DialogDescription>Lasciaci un recapito: ti richiamiamo il prima possibile.</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 mt-1">
                <div className="sm:col-span-2"><Field label="Nome *"><Input value={lf.nome} onChange={setL("nome")} data-testid="ql-nome" /></Field></div>
                <Field label="Telefono *"><Input value={lf.telefono} onChange={setL("telefono")} data-testid="ql-telefono" /></Field>
                <Field label="Email *"><Input type="email" value={lf.email} onChange={setL("email")} data-testid="ql-email" /></Field>
                <div className="sm:col-span-2"><Field label="Indirizzo *"><AddressAutocomplete value={lf.comune_cap} onChange={(v) => setLf((s) => ({ ...s, comune_cap: v }))} onSelect={(addr) => setLf((s) => ({ ...s, comune_cap: addr }))} placeholder="Inizia a digitare via, civico e città…" data-testid="ql-comune" /></Field></div>
                <Field label="Tipologia">
                  <Select value={lf.tipologia} onValueChange={(v) => setLf((s) => ({ ...s, tipologia: v }))}>
                    <SelectTrigger data-testid="ql-tipologia"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOLOGIE.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`ql-tipologia-${o.v}`}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2"><Field label="Messaggio (facoltativo)"><Textarea value={lf.messaggio} onChange={setL("messaggio")} rows={3} data-testid="ql-messaggio" /></Field></div>
              </div>
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                <input tabIndex={-1} autoComplete="off" value={lf.hp} onChange={setL("hp")} data-testid="ql-hp" />
              </div>
              <DialogFooter>
                <Button onClick={submitLead} disabled={busy} data-testid="ql-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Richiedi di essere ricontattato"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-center" data-testid="ql-success">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg font-bold">Richiesta ricevuta!</h3>
              <p className="text-sm text-muted-foreground mt-1">Ti ricontatteremo il prima possibile. Grazie!</p>
              <Button variant="outline" className="mt-4" onClick={() => setOpenLead(false)} data-testid="ql-close">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
